// JS for LPv2 Main Product Options Bundle block
export function initOptionsBundleBlock() {
  const containers = document.querySelectorAll('.lpv2-main-product-options-bundle');
  if (containers.length === 0) return;

  const stateManager = window.lpv2MainProductStateManager || new window.LPV2MainProductStateManager();

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    const moneyFormat =
      (window.LPV2MainProductInitialState && window.LPV2MainProductInitialState.moneyFormat) ||
      window.moneyFormat;
    if (window.Shopify && window.Shopify.formatMoney && moneyFormat) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    return (cents / 100).toLocaleString(undefined, { style: 'currency', currency });
  }

  function replacePriceInText(str, priceFormatted) {
    if (!str) return '';
    return str.replace(/(?:[£$€¥]|CAD\s*)?\s*[\d,.]+(?:\s*(?:[£$€¥]|CAD))?/, priceFormatted);
  }

  function findPlanInGroups(planId) {
    if (!planId) return null;
    const groups = window.LPV2MainProductInitialState?.sellingPlanGroups || [];
    for (const group of groups) {
      const found = (group.selling_plans || []).find(p => String(p.id) === String(planId));
      if (found) return found;
    }
    return null;
  }

  function findPlanByMonthsInGroups(months) {
    const groups = window.LPV2MainProductInitialState?.sellingPlanGroups || [];
    const monthsStr = String(months);
    for (const group of groups) {
      for (const plan of group.selling_plans || []) {
        const name = (plan.name || '').toLowerCase();
        if (months === 12 && (name.includes('annual') || name.includes('year') || name.includes('12') || name.includes('9'))) {
          return plan;
        }
        if (name.includes(monthsStr)) {
          return plan;
        }
      }
    }
    return null;
  }

  function getFirstPlanFromGroups() {
    const groups = window.LPV2MainProductInitialState?.sellingPlanGroups || [];
    for (const group of groups) {
      if (group.selling_plans && group.selling_plans.length > 0) {
        return group.selling_plans[0];
      }
    }
    return null;
  }

  function findSpaForMonths(variant, months) {
    if (!variant || !Array.isArray(variant.selling_plan_allocations) || variant.selling_plan_allocations.length === 0) {
      return null;
    }
    const spas = variant.selling_plan_allocations;
    const monthsStr = String(months);

    // 1. Try matching by selling plan name or option values
    const matched = spas.find(spa => {
      const name = ((spa.selling_plan && spa.selling_plan.name) || '').toLowerCase();
      const optVal = ((spa.selling_plan && spa.selling_plan.options && spa.selling_plan.options[0]?.value) || '').toLowerCase();
      if (months === 12 && (name.includes('annual') || name.includes('year') || name.includes('12') || name.includes('9') || optVal.includes('9') || optVal.includes('12') || optVal.includes('annual'))) {
        return true;
      }
      return name.includes(monthsStr) || optVal.includes(monthsStr);
    });
    if (matched) return matched;

    // 2. Fallback by index if multiple plans are allocated
    if (months === 3 && spas[0]) return spas[0];
    if (months === 6 && spas[1]) return spas[1];
    if (months === 12 && (spas[2] || spas[1])) return spas[2] || spas[1];

    return null;
  }

  function getSpaPrice(spa) {
    if (!spa) return null;
    if (typeof spa.price === 'number' && spa.price > 0) return spa.price;
    if (typeof spa.per_delivery_price === 'number' && spa.per_delivery_price > 0) return spa.per_delivery_price;
    return null;
  }

  function getRecurringPriceForVariant(variant, months, container = null) {
    if (!variant) return null;
    const spa = findSpaForMonths(variant, months);
    if (spa) {
      if (typeof spa.recurring_price === 'number' && spa.recurring_price > 0) {
        return spa.recurring_price;
      }
      if (Array.isArray(spa.price_adjustments) && spa.price_adjustments.length > 0) {
        const lastAdj = spa.price_adjustments[spa.price_adjustments.length - 1];
        if (typeof lastAdj.price === 'number' && lastAdj.price > 0) {
          return lastAdj.price;
        }
      }
    }
    const plan = findSellingPlanForVariant(variant, months);
    if (plan && Array.isArray(plan.price_adjustments)) {
      const fixedAdj = plan.price_adjustments.find(a => a.value_type === 'price' || a.value_type === 'fixed_amount');
      if (fixedAdj && typeof fixedAdj.value === 'number' && fixedAdj.value > 0) {
        let rate = 1.0;
        if (container) {
          const containerRate = parseFloat(container.dataset.conversionRate);
          if (!isNaN(containerRate) && containerRate > 0) rate = containerRate;
        }
        const curr =
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
          (container ? container.dataset.currency : 'GBP');
        if (curr !== 'GBP' && rate && rate !== 1.0) {
          return Math.round(fixedAdj.value * rate);
        }
        return fixedAdj.value;
      }
    }
    return null;
  }

  function findSellingPlanForVariant(variant, months) {
    if (!variant) return null;
    const spa = findSpaForMonths(variant, months);
    if (spa) {
      const planId = spa.selling_plan_id || (spa.selling_plan && spa.selling_plan.id);
      if (planId) {
        const fullPlan = findPlanInGroups(planId);
        return fullPlan || spa.selling_plan || { id: planId };
      }
    }

    const planFromGroups = findPlanByMonthsInGroups(months);
    if (planFromGroups) return planFromGroups;

    return getFirstPlanFromGroups();
  }

  function getLowestSubForVariant(variant) {
    if (!variant) return null;
    let lowest = null;
    if (Array.isArray(variant.selling_plan_allocations)) {
      variant.selling_plan_allocations.forEach(spa => {
        const p = spa.price || spa.per_delivery_price;
        if (typeof p === 'number' && p > 0) {
          if (lowest === null || p < lowest) lowest = p;
        }
      });
    }
    return lowest;
  }

  containers.forEach(function (container) {
    let isOnetime = false;

    const colorSwatches = container.querySelectorAll('.lpv2-bundle-swatch');
    const selectedColorLabel = container.querySelector('[data-bundle-selected-color]');
    const supplyCards = container.querySelectorAll('.lpv2-bundle-card');
    const supplyLabel = container.querySelector('[data-bundle-supply-label]');
    const supplyPriceEl = container.querySelector('[data-bundle-supply-price]');
    const totalPriceEl = container.querySelector('[data-bundle-total-price]');
    const totalCompareEl = container.querySelector('[data-bundle-total-compare]');
    const atcBtn = container.querySelector('[data-bundle-atc-btn]');
    const notifyBtn = container.querySelector('[data-bundle-notify-btn]');
    const btnPriceEl = container.querySelector('[data-bundle-btn-price]');
    const btnLabelEl = atcBtn ? atcBtn.querySelector('.lpv2-bundle-btn__label') : null;
    const onetimeToggle = container.querySelector('[data-bundle-onetime-toggle]');
    const onetimeCards = container.querySelectorAll('.lpv2-onetime-plan-card');
    const onetimeSubCard = container.querySelector('.lpv2-onetime-plan-card--subscription');
    const onetimeOnetimeCard = container.querySelector('.lpv2-onetime-plan-card--onetime');
    const onetimeSubPriceEl = container.querySelector('[data-onetime-sub-price]');
    const onetimeSubCompareEl = container.querySelector('[data-onetime-sub-compare]');
    const onetimeCardPriceEl = container.querySelector('[data-onetime-card-price]');
    const onetimeSubBilledEl = container.querySelector('[data-onetime-sub-billed-text]');
    const devicePriceEls = container.querySelectorAll('.lpv2-bundle-summary [data-bundle-device-price]');
    const deviceCardPriceEl = container.querySelector('[data-bundle-device-card-price]');

    function getConversionRate() {
      const containerRate = parseFloat(container.dataset.conversionRate);
      if (!isNaN(containerRate) && containerRate > 0) return containerRate;
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
        const r = parseFloat(window.Shopify.currency.rate);
        if (!isNaN(r) && r > 0) return r;
      }
      const curr =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || container.dataset.currency || 'GBP';
      if (curr === 'EUR') return 1.17;
      if (curr === 'USD') return 1.30;
      if (curr === 'CAD') return 1.75;
      if (curr === 'AUD') return 1.95;
      if (curr === 'CHF') return 1.13;
      if (curr === 'AED') return 4.77;
      if (curr === 'SAR') return 4.88;
      return 1.0;
    }

    function calcDevicePrice(v3m) {
      if (v3m) {
        const spa3 = findSpaForMonths(v3m, 3);
        const card1 = container.querySelector('.lpv2-bundle-card[data-supply-months="3"]');
        const subPrice =
          getSpaPrice(spa3) ||
          parseInt(card1?.dataset?.supplyTotal, 10) ||
          (typeof v3m.price === 'number' ? Math.round(v3m.price * 0.8) : null);
        const rec =
          getRecurringPriceForVariant(v3m, 3, container) ||
          parseInt(card1?.dataset?.supplyPrice, 10) ||
          parseInt(container.dataset.refill3mPrice, 10);
        if (typeof subPrice === 'number' && typeof rec === 'number' && subPrice > rec) {
          return subPrice - rec;
        }
      }
      return parseInt(container.dataset.devicePrice, 10) || Math.round(6000 * getConversionRate());
    }

    let deviceBreakdownPrice = parseInt(container.dataset.devicePrice, 10) || Math.round(6000 * getConversionRate());
    let devicePrice = deviceBreakdownPrice;
    let deviceCardPrice = parseInt(container.dataset.deviceCardPrice, 10) || deviceBreakdownPrice;

    const onetimePrice = parseInt(container.dataset.onetimePrice, 10) || Math.round(12000 * getConversionRate());
    let comparePrice = parseInt(container.dataset.comparePrice, 10) || Math.round(12000 * getConversionRate());

    function getSupplyVariantsForColor(colorValue) {
      if (!window.LPV2ProductVariants || window.LPV2ProductVariants.length === 0) {
        return { v3m: null, v6m: null, v12m: null };
      }
      const colorMatches = window.LPV2ProductVariants.filter(v => {
        return (
          v.option1 === colorValue ||
          v.option2 === colorValue ||
          v.option3 === colorValue ||
          (!v.option1 && !v.option2 && !v.option3)
        );
      });
      const pool = colorMatches.length > 0 ? colorMatches : window.LPV2ProductVariants;

      let v3m = null,
        v6m = null,
        v12m = null;

      pool.forEach(v => {
        const text = (
          (v.title || '') +
          ' ' +
          (v.options ? v.options.join(' ') : '')
        ).toLowerCase();
        if (text.includes('12') || text.includes('year') || text.includes('annual')) {
          v12m = v;
        } else if (text.includes('6')) {
          v6m = v;
        } else if (text.includes('3')) {
          v3m = v;
        }
      });

      if (!v3m && pool[0]) v3m = pool[0];
      if (!v6m) v6m = pool[1] || v3m;
      if (!v12m) v12m = pool[2] || pool[1] || v3m;

      return { v3m, v6m, v12m };
    }

    function updateDeviceCardImage(colorValue, v3m = null, variant = null) {
      const deviceCardImg = container.querySelector('[data-device-card-img]');
      if (!deviceCardImg) return;

      if (colorValue) {
        const matchingSwatch = Array.from(colorSwatches).find(
          s => s.dataset.colorValue === colorValue
        );
        if (matchingSwatch) {
          if (matchingSwatch.dataset.deviceImage) {
            deviceCardImg.src = matchingSwatch.dataset.deviceImage;
            return;
          }
          const swatchIdx = Array.from(colorSwatches).indexOf(matchingSwatch);
          const overrideKey = `deviceImage${swatchIdx + 1}`;
          if (container.dataset[overrideKey]) {
            deviceCardImg.src = container.dataset[overrideKey];
            return;
          }
        }
      }

      const fallbackUrl =
        (variant && (variant.image_url || (variant.featured_image && (variant.featured_image.src || variant.featured_image)))) ||
        (v3m && (v3m.image_url || (v3m.featured_image && (v3m.featured_image.src || v3m.featured_image))));
      if (fallbackUrl) {
        deviceCardImg.src = fallbackUrl;
      }
    }

    function updateCardBullets(card, bullets) {
      if (!card) return;
      const bulletsList = card.querySelector('.lpv2-bundle-bullets');
      if (!bulletsList) return;
      if (!bulletsList.dataset.defaultHtml) {
        bulletsList.dataset.defaultHtml = bulletsList.innerHTML;
      }
      if (bullets && Array.isArray(bullets) && bullets.length > 0) {
        bulletsList.innerHTML = '';
        bullets.forEach(line => {
          const li = document.createElement('li');
          li.className = 'lpv2-bundle-bullet';
          li.innerHTML =
            '<span class="lpv2-bundle-bullet__icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 9" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"/></svg></span><span class="lpv2-bundle-bullet__text">' +
            line +
            '</span>';
          bulletsList.appendChild(li);
        });
      } else if (bulletsList.dataset.defaultHtml && bulletsList.innerHTML !== bulletsList.dataset.defaultHtml) {
        bulletsList.innerHTML = bulletsList.dataset.defaultHtml;
      }
    }

    function updateBilledBullet(card, price, is9m = false) {
      if (!card || typeof price !== 'number') return;
      const bulletEl = card.querySelector('[data-supply-billed-text]');
      if (bulletEl) {
        const priceSpan = bulletEl.querySelector('[data-billed-price]');
        if (priceSpan) {
          priceSpan.textContent = formatMoney(price);
          return;
        }
        let tpl = bulletEl.dataset.billedTemplate;
        if (is9m && bulletEl.dataset.billedTemplate9m) {
          tpl = bulletEl.dataset.billedTemplate9m;
        }
        if (tpl) {
          bulletEl.textContent = tpl.replace('[price]', formatMoney(price));
        } else {
          bulletEl.textContent = replacePriceInText(bulletEl.textContent, formatMoney(price));
        }
      }
    }

    function updateSubprice(card, perRefillPrice) {
      if (!card || typeof perRefillPrice !== 'number') return;
      const subpriceEl = card.querySelector('.lpv2-bundle-card__subprice');
      if (subpriceEl) {
        const priceSpan = subpriceEl.querySelector('.lpv2-bundle-subprice-price');
        if (priceSpan) {
          priceSpan.textContent = formatMoney(perRefillPrice);
          return;
        }
        const tpl = subpriceEl.dataset.perRefillTemplate;
        if (tpl) {
          subpriceEl.textContent = tpl.replace('[price]', formatMoney(perRefillPrice));
        } else {
          subpriceEl.textContent = `${formatMoney(perRefillPrice)} per refill`;
        }
      }
    }

    function updateSupplyCardsForVariants(v3m, v6m, v12m) {
      if (!v3m) return;
      if (!v6m) v6m = v3m;
      if (!v12m) v12m = v6m || v3m;

      // Compute device price using Formula: Variant price - recurring price
      devicePrice = calcDevicePrice(v3m);
      deviceBreakdownPrice = devicePrice;
      deviceCardPrice = devicePrice;

      if (deviceCardPriceEl && !isOnetime) {
        deviceCardPriceEl.textContent = formatMoney(deviceCardPrice);
      }
      devicePriceEls.forEach(el => {
        el.textContent = formatMoney(devicePrice);
      });

      const card1 = container.querySelector('.lpv2-bundle-card[data-supply-months="3"]');
      const card2 = container.querySelector('.lpv2-bundle-card[data-supply-months="6"]');
      const card3 = container.querySelector('.lpv2-bundle-card[data-supply-months="12"]');

      // --- CARD 1: 3-Months Supply ---
      const rec3 = getRecurringPriceForVariant(v3m, 3, container);
      const spa3 = findSpaForMonths(v3m, 3);
      const spa3Price = getSpaPrice(spa3);
      const total3 = spa3Price || (v3m.price ? Math.round(v3m.price * 0.8) : onetimePrice);
      const refill3 = rec3 || Math.max(0, total3 - devicePrice);

      // Dynamic compare price for Card 1 (3-months supply)
      let compare1 = spa3?.compare_at_price;
      if (!compare1 || compare1 <= total3) {
        compare1 = (v3m.compare_at_price && v3m.compare_at_price > total3)
          ? v3m.compare_at_price
          : (v3m.price || onetimePrice);
      }

      if (card1) {
        card1.dataset.variantId = v3m.id;
        if (v3m.price) card1.dataset.onetimePrice = v3m.price;
        card1.dataset.comparePrice = compare1;
        if (v3m.image_url) {
          const img = card1.querySelector('[data-supply-img="3"]');
          if (img) img.src = v3m.image_url;
        }
        const planId3 = spa3?.selling_plan_id || spa3?.selling_plan?.id;
        if (planId3) card1.dataset.planId = planId3; else delete card1.dataset.planId;
        card1.dataset.supplyTotal = total3;
        card1.dataset.supplyPrice = refill3;
        const priceEl1 = card1.querySelector('.lpv2-bundle-card__price');
        if (priceEl1) priceEl1.textContent = formatMoney(refill3);
        updateSubprice(card1, refill3);
        if (v3m.filter_supply_list) {
          updateCardBullets(card1, v3m.filter_supply_list);
        } else {
          updateCardBullets(card1, null);
          updateBilledBullet(card1, refill3);
        }

        // Also update One-time layout cards (Figma node 4197:1540)
        const compare3 = v3m.compare_at_price || v3m.price || onetimePrice;
        const onetimeVariantPrice = v3m.price || onetimePrice;
        if (onetimeSubPriceEl) onetimeSubPriceEl.textContent = formatMoney(total3);
        if (onetimeSubCompareEl) onetimeSubCompareEl.textContent = formatMoney(compare3);
        if (onetimeCardPriceEl) onetimeCardPriceEl.textContent = formatMoney(onetimeVariantPrice);
        if (onetimeSubBilledEl) {
          const priceSpan = onetimeSubBilledEl.querySelector('[data-billed-price]');
          if (priceSpan) {
            priceSpan.textContent = formatMoney(refill3);
          } else {
            const tpl = onetimeSubBilledEl.dataset.billedTemplate;
            if (tpl) {
              onetimeSubBilledEl.textContent = tpl.replace('[price]', formatMoney(refill3));
            } else {
              onetimeSubBilledEl.textContent = replacePriceInText(
                onetimeSubBilledEl.textContent,
                formatMoney(refill3)
              );
            }
          }
        }
        if (onetimeToggle && !isOnetime) {
          const priceSpan = onetimeToggle.querySelector('.lpv2-bundle-toggle-price');
          if (priceSpan) {
            priceSpan.textContent = formatMoney(onetimeVariantPrice);
          } else {
            const tpl = onetimeToggle.dataset.labelOnetime || 'I prefer a one-time purchase ([price])';
            onetimeToggle.textContent = replacePriceInText(tpl, formatMoney(onetimeVariantPrice));
          }
        }
        const preferOnetimeBtns = document.querySelectorAll('.js-prefer-one-time');
        preferOnetimeBtns.forEach(btn => {
          if (!btn.dataset.tpl) {
            btn.dataset.tpl = btn.textContent;
          }
          btn.textContent = replacePriceInText(btn.dataset.tpl, formatMoney(onetimeVariantPrice));
        });
      }

      // --- CARD 2: 6-Months Supply ---
      const rec6 = getRecurringPriceForVariant(v6m, 6, container);
      const spa6 = findSpaForMonths(v6m, 6);
      const spa6Price = getSpaPrice(spa6);
      let total6 = null;
      let refill6 = null;

      if (rec6) {
        refill6 = rec6;
        total6 = spa6Price || (devicePrice + refill6);
      } else if (v6m.id !== v3m.id && v6m.price) {
        // Distinct variant for 6m
        total6 = spa6Price || Math.round(v6m.price * 0.8);
        refill6 = Math.max(0, total6 - devicePrice);
      } else if (spa6Price && spa6 !== spa3) {
        // Same variant, distinct 6m selling plan
        total6 = spa6Price;
        refill6 = Math.max(0, total6 - devicePrice);
      } else {
        // Proportional 6m calculation: 2 refills
        refill6 = Math.round(refill3 * 1.83333);
        total6 = devicePrice + refill6;
      }
      const perRefill6 = Math.round(refill6 / 2);

      // Dynamic compare price for Card 2 (6-months supply)
      let compare2 = spa6?.compare_at_price;
      if (!compare2 || compare2 <= total6) {
        if (v6m.compare_at_price && v6m.compare_at_price > total6) {
          compare2 = v6m.compare_at_price;
        } else if (v6m.id !== v3m.id && v6m.price && v6m.price > total6) {
          compare2 = v6m.price;
        } else {
          const fullRefill3 = parseInt(card1?.dataset.supplyPrice, 10) || refill3;
          compare2 = devicePrice + Math.round(fullRefill3 * 2);
        }
      }

      if (card2) {
        card2.dataset.variantId = v6m.id;
        if (v6m.price) card2.dataset.onetimePrice = v6m.price;
        card2.dataset.comparePrice = compare2;
        if (v6m.image_url) {
          const img = card2.querySelector('[data-supply-img="6"]');
          if (img) img.src = v6m.image_url;
        }
        const planId6 = spa6?.selling_plan_id || spa6?.selling_plan?.id;
        if (planId6) card2.dataset.planId = planId6; else delete card2.dataset.planId;
        card2.dataset.supplyTotal = total6;
        card2.dataset.supplyPrice = refill6;
        const priceEl2 = card2.querySelector('.lpv2-bundle-card__price');
        if (priceEl2) priceEl2.textContent = formatMoney(refill6);
        updateSubprice(card2, perRefill6);
        if (v6m.filter_supply_list) {
          updateCardBullets(card2, v6m.filter_supply_list);
        } else {
          updateCardBullets(card2, null);
          updateBilledBullet(card2, refill6);
        }
      }

      // --- CARD 3: 12-Months Supply ---
      const rec12 = getRecurringPriceForVariant(v12m, 12, container);
      const spa12 = findSpaForMonths(v12m, 12);
      const spa12Price = getSpaPrice(spa12);
      let total12 = null;
      let refill12 = null;

      if (rec12) {
        refill12 = rec12;
        total12 = spa12Price || (devicePrice + refill12);
      } else if (v12m.id !== v3m.id && v12m.id !== v6m.id && v12m.price) {
        // Distinct variant for 12m
        total12 = spa12Price || Math.round(v12m.price * 0.8);
        refill12 = Math.max(0, total12 - devicePrice);
      } else if (spa12Price && spa12 !== spa3 && spa12 !== spa6) {
        // Same variant, distinct 12m/9m selling plan
        total12 = spa12Price;
        refill12 = Math.max(0, total12 - devicePrice);
      } else {
        // Proportional 12m calculation: 4 refills
        refill12 = Math.round(refill3 * 3.0);
        total12 = devicePrice + refill12;
      }
      const planName12 = ((spa12 && spa12.selling_plan && spa12.selling_plan.name) || '').toLowerCase();
      const is9m = planName12.includes('9');
      const perRefill12 = Math.round(refill12 / (is9m ? 3 : 4));

      // Dynamic compare price for Card 3 (12-months supply)
      let compare12 = spa12?.compare_at_price;
      if (!compare12 || compare12 <= total12) {
        if (v12m.compare_at_price && v12m.compare_at_price > total12) {
          compare12 = v12m.compare_at_price;
        } else if (v12m.id !== v3m.id && v12m.id !== v6m.id && v12m.price && v12m.price > total12) {
          compare12 = v12m.price;
        } else {
          const fullRefill3 = parseInt(card1?.dataset.supplyPrice, 10) || refill3;
          compare12 = devicePrice + Math.round(fullRefill3 * (is9m ? 3 : 4));
        }
      }

      if (card3) {
        card3.dataset.variantId = v12m.id;
        if (v12m.price) card3.dataset.onetimePrice = v12m.price;
        card3.dataset.comparePrice = compare12;
        if (v12m.image_url) {
          const img = card3.querySelector('[data-supply-img="12"]');
          if (img) img.src = v12m.image_url;
        }
        const planId12 = spa12?.selling_plan_id || spa12?.selling_plan?.id;
        if (planId12) card3.dataset.planId = planId12; else delete card3.dataset.planId;
        card3.dataset.supplyTotal = total12;
        card3.dataset.supplyPrice = refill12;
        const priceEl3 = card3.querySelector('.lpv2-bundle-card__price');
        if (priceEl3) priceEl3.textContent = formatMoney(refill12);
        updateSubprice(card3, perRefill12);
        if (v12m.filter_supply_list) {
          updateCardBullets(card3, v12m.filter_supply_list);
        } else {
          updateCardBullets(card3, null);
          updateBilledBullet(card3, refill12, is9m);
        }
      }
    }

    // Helper to keep summary rows strictly in sync: Filter supply price = Card refill price, Device price = Variant Total - Refill price
    function updateSummaryPrices(activeCard) {
      if (!activeCard) return;
      const supplyMonths = activeCard.dataset.supplyMonths || 3;
      const cardSupplyTotal = parseInt(activeCard.dataset.supplyTotal, 10);
      const cardSupplyPrice = parseInt(activeCard.dataset.supplyPrice, 10);
      const cardOnetimePrice = parseInt(activeCard.dataset.onetimePrice, 10) || onetimePrice;

      if (isOnetime) {
        // ONE-TIME MODE (Figma node 4197:1540 & Screenshot 2):
        // Total price = cardOnetimePrice (£120 / $200)
        // Filter supply price = 3-month refill price (£36 / $36)
        // Device price = Total onetime price - 3-month refill price
        const card1 = container.querySelector('.lpv2-bundle-card[data-supply-months="3"]');
        const refill3Price =
          (card1 ? parseInt(card1.dataset.supplyPrice, 10) : null) ||
          parseInt(container.dataset.refill3mPrice, 10) ||
          Math.round(3600 * getConversionRate());

        const baseDevicePrice = calcDevicePrice(initialVariant || window.LPV2ProductVariants?.[0]);
        devicePrice = baseDevicePrice;
        deviceBreakdownPrice = baseDevicePrice;
        deviceCardPrice = baseDevicePrice;

        if (devicePriceEls) {
          devicePriceEls.forEach(el => {
            el.textContent = formatMoney(baseDevicePrice);
          });
        }
        if (deviceCardPriceEl) {
          deviceCardPriceEl.textContent = formatMoney(baseDevicePrice);
        }

        if (supplyLabel) {
          const items = supplyLabel.querySelectorAll('.lpv2-bundle-supply-label-item');
          if (items.length > 0) {
            items.forEach(item => {
              item.style.display = item.dataset.months === '3' ? '' : 'none';
            });
          } else {
            supplyLabel.textContent =
              (card1 && card1.dataset.supplyBreakdownLabel) || 'Filter supply (3-month)';
          }
        }
        if (supplyPriceEl) {
          supplyPriceEl.textContent = formatMoney(refill3Price);
        }
        if (totalPriceEl) {
          totalPriceEl.textContent = formatMoney(cardOnetimePrice);
        }
        const activeVariantId = activeCard.dataset.variantId || container.dataset.selectedVariantId;
        const activeVariant = window.LPV2ProductVariants
          ? window.LPV2ProductVariants.find(v => String(v.id) === String(activeVariantId))
          : null;
        const onetimeCompare = activeVariant?.compare_at_price;
        if (totalCompareEl) {
          if (onetimeCompare && onetimeCompare > cardOnetimePrice) {
            totalCompareEl.style.display = '';
            totalCompareEl.textContent = formatMoney(onetimeCompare);
          } else {
            totalCompareEl.style.display = 'none';
          }
        }
        if (btnPriceEl) {
          btnPriceEl.textContent = formatMoney(cardOnetimePrice);
        }
      } else {
        // SUBSCRIPTION MODE:
        // 1. Filter supply price ALWAYS strictly matches the active card's price:
        const filterSupplyPrice =
          (!isNaN(cardSupplyPrice) && cardSupplyPrice > 0)
            ? cardSupplyPrice
            : Math.round(3600 * getConversionRate());

        // 2. Supply total is the variant's subscription price (or fallback device + refill):
        const supplyTotal = cardSupplyTotal || (devicePrice + filterSupplyPrice);

        // 3. Device price is dynamically calculated: Variant subscription total - Refill price:
        const activeDevicePrice = Math.max(0, supplyTotal - filterSupplyPrice);
        devicePrice = activeDevicePrice;
        deviceBreakdownPrice = activeDevicePrice;
        deviceCardPrice = activeDevicePrice;

        if (devicePriceEls) {
          devicePriceEls.forEach(el => {
            el.textContent = formatMoney(activeDevicePrice);
          });
        }
        if (deviceCardPriceEl) {
          deviceCardPriceEl.textContent = formatMoney(activeDevicePrice);
        }

        if (supplyLabel) {
          const items = supplyLabel.querySelectorAll('.lpv2-bundle-supply-label-item');
          if (items.length > 0) {
            items.forEach(item => {
              item.style.display = item.dataset.months === String(supplyMonths) ? '' : 'none';
            });
          } else {
            supplyLabel.textContent =
              activeCard.dataset.supplyBreakdownLabel || `Filter supply (${supplyMonths}-month)`;
          }
        }
        if (supplyPriceEl) {
          supplyPriceEl.textContent = formatMoney(filterSupplyPrice);
        }
        if (totalPriceEl) {
          totalPriceEl.textContent = formatMoney(supplyTotal);
        }

        let activeComparePrice = parseInt(activeCard.dataset.comparePrice, 10);
        if (!activeComparePrice || isNaN(activeComparePrice)) {
          const activeVariantId = activeCard.dataset.variantId || container.dataset.selectedVariantId;
          const activeVariant = window.LPV2ProductVariants
            ? window.LPV2ProductVariants.find(v => String(v.id) === String(activeVariantId))
            : null;
          if (activeVariant) {
            activeComparePrice =
              (activeVariant.compare_at_price && activeVariant.compare_at_price > supplyTotal)
                ? activeVariant.compare_at_price
                : (activeVariant.price && activeVariant.price > supplyTotal
                    ? activeVariant.price
                    : (activeVariant.price || cardOnetimePrice));
          } else {
            activeComparePrice = cardOnetimePrice || comparePrice;
          }
        }

        if (activeComparePrice) {
          comparePrice = activeComparePrice;
          container.dataset.comparePrice = activeComparePrice;
        }

        if (totalCompareEl) {
          if (activeComparePrice && activeComparePrice > supplyTotal) {
            totalCompareEl.style.display = '';
            totalCompareEl.textContent = formatMoney(activeComparePrice);
          } else {
            totalCompareEl.style.display = 'none';
          }
        }
        if (btnPriceEl) {
          btnPriceEl.textContent = formatMoney(supplyTotal);
        }
      }
    }

    function isVariantAvailable(variant) {
      if (!variant) return true;
      let canContinueOverselling = false;
      try {
        const dataEls = document.querySelectorAll('script[id^="lpv2-preorder-data-"]');
        if (dataEls.length > 0) {
          const variantPreorderData = JSON.parse(dataEls[0].textContent);
          canContinueOverselling =
            variantPreorderData[String(variant.id)] &&
            variantPreorderData[String(variant.id)].continueSellingWhenOutOfStock === true;
        }
      } catch (e) {}
      return Boolean(canContinueOverselling || variant.available);
    }

    function updateAvailability(variant) {
      if (!variant) return;

      const isAvailable = isVariantAvailable(variant);

      if (isAvailable) {
        if (atcBtn) atcBtn.style.display = '';
        if (notifyBtn) notifyBtn.style.display = 'none';
      } else {
        if (atcBtn) atcBtn.style.display = 'none';
        if (notifyBtn) notifyBtn.style.display = 'flex';
      }

      // Sync availability to global stateManager (updates sticky ATC)
      if (stateManager && typeof stateManager.setState === 'function') {
        stateManager.setState({ available: isAvailable });
      }

      // Update variant_id in notify me modal
      const notifyModals = document.querySelectorAll('[data-modal="product-notify"]');
      notifyModals.forEach(modal => {
        let variantInput = modal.querySelector('input[name="variant_id"]');
        if (!variantInput) {
          variantInput = document.createElement('input');
          variantInput.type = 'hidden';
          variantInput.name = 'variant_id';
          const form = modal.querySelector('form');
          if (form) form.appendChild(variantInput);
        }
        if (variantInput) {
          variantInput.value = variant.id;
        }
      });
    }

    function initNotifyModalForm() {
      const notifyForms = document.querySelectorAll('[data-modal="product-notify"] form');
      notifyForms.forEach(form => {
        if (form.dataset.bisInitialized) return;
        form.dataset.bisInitialized = 'true';

        form.addEventListener('submit', function (e) {
          e.preventDefault();
          const emailInput = form.querySelector('input[type="email"]');
          const notifyButton =
            form.querySelector('button[type="submit"]') || form.querySelector('button');
          const messageContainer =
            form.querySelector('#bis-message-container') ||
            form.querySelector('.bis-message-container');

          if (!emailInput || !emailInput.value.trim()) return;
          const email = emailInput.value.trim();

          let variantId = form.querySelector('input[name="variant_id"]')?.value;
          if (!variantId && window.LPV2MainProductInitialState) {
            variantId = window.LPV2MainProductInitialState.selectedVariantId;
          }
          if (!variantId) {
            const activeCard = container.querySelector('.lpv2-bundle-card.active');
            variantId = activeCard?.dataset.variantId || container.dataset.selectedVariantId;
          }

          if (!variantId) return;

          const originalText = notifyButton ? notifyButton.textContent : 'Notify Me';
          if (notifyButton) {
            notifyButton.textContent = 'Sending...';
            notifyButton.disabled = true;
          }
          if (messageContainer) {
            messageContainer.textContent = '';
            messageContainer.style.display = 'none';
          }

          const myHeaders = new Headers();
          myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

          const urlencoded = new URLSearchParams();
          urlencoded.append('a', 'StA33h');
          urlencoded.append('variant', variantId);
          urlencoded.append('platform', 'shopify');
          urlencoded.append('email', email);

          fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe', {
            method: 'POST',
            headers: myHeaders,
            body: urlencoded,
            redirect: 'follow'
          })
            .then(res => res.json())
            .then(data => {
              if (messageContainer) {
                messageContainer.style.display = 'block';
                if (data.success) {
                  messageContainer.textContent =
                    'Success! You will be notified when the product is back in stock.';
                  messageContainer.style.color = '#2e7d32';
                  emailInput.value = '';
                } else {
                  messageContainer.textContent =
                    'Error: ' + (data.error_message || 'Unable to subscribe');
                  messageContainer.style.color = '#c62828';
                }
              }
            })
            .catch(() => {
              if (messageContainer) {
                messageContainer.style.display = 'block';
                messageContainer.textContent =
                  'Error: Unable to process your request. Please try again later.';
                messageContainer.style.color = '#c62828';
              }
            })
            .finally(() => {
              if (notifyButton) {
                notifyButton.textContent = originalText;
                notifyButton.disabled = false;
              }
            });
        });
      });
    }

    // --- Initial State Setup on Load ---
    const initialActiveCard =
      container.querySelector('.lpv2-bundle-card.active') ||
      container.querySelector('.lpv2-bundle-card');
    const initialMonths = initialActiveCard
      ? parseInt(initialActiveCard.dataset.supplyMonths, 10)
      : 3;
    const initialVariantId =
      initialActiveCard?.dataset.variantId || container.dataset.selectedVariantId;

    const initialVariant = window.LPV2ProductVariants
      ? window.LPV2ProductVariants.find(v => String(v.id) === String(initialVariantId))
      : null;

    let initialPlanId = null;
    if (initialVariant) {
      const p = findSellingPlanForVariant(initialVariant, initialMonths);
      if (p && p.id) {
        initialPlanId = p.id;
        if (initialActiveCard) initialActiveCard.dataset.planId = p.id;
      } else if (initialActiveCard) {
        delete initialActiveCard.dataset.planId;
      }
    } else {
      initialPlanId = initialActiveCard?.dataset.planId || container.dataset.selectedPlanId;
    }

    const initialPlan =
      findPlanInGroups(initialPlanId) || (initialPlanId ? { id: initialPlanId } : null);

    stateManager.setState({
      purchaseOption: 'subscription',
      selectedVariant: initialVariant || stateManager.getState().selectedVariant,
      selectedVariantId: initialVariantId,
      selectedPlanId: initialPlanId,
      selectedPlan: initialPlan,
      selectedFreebies: [],
      available: isVariantAvailable(initialVariant)
    });

    const initForm = document.querySelector('#lpv2-add-to-cart-form');
    if (initForm) {
      const vInput = initForm.querySelector('input[name="id"]');
      const pInput = initForm.querySelector('input[name="selling_plan"]');
      if (vInput && initialVariantId) vInput.value = initialVariantId;
      if (pInput && initialPlanId) pInput.value = initialPlanId;
    }

    let lastHandledVariantId = initialVariantId;

    // Format device price elements (device card and summary breakdown)
    devicePriceEls.forEach(el => {
      el.textContent = formatMoney(deviceBreakdownPrice);
    });

    if (deviceCardPriceEl) {
      deviceCardPriceEl.textContent = formatMoney(deviceCardPrice);
    }

    // Update supply card prices for the initial variant
    const initialColor = initialVariant
      ? (initialVariant.option1 || initialVariant.option2 || initialVariant.option3)
      : null;
    if (initialColor) {
      const { v3m, v6m, v12m } = getSupplyVariantsForColor(initialColor);
      updateSupplyCardsForVariants(v3m, v6m, v12m);
      updateDeviceCardImage(initialColor, v3m, initialVariant);
    }

    // Initialize summary prices based on initial active card
    updateSummaryPrices(initialActiveCard);
    updateAvailability(initialVariant);
    initNotifyModalForm();

    // --- 1. Colour Swatch Selection ---
    colorSwatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        const colorValue = swatch.dataset.colorValue;
        if (!colorValue) return;

        colorSwatches.forEach(s => {
          s.classList.remove('active');
          s.setAttribute('aria-checked', 'false');
        });
        swatch.classList.add('active');
        swatch.setAttribute('aria-checked', 'true');

        if (selectedColorLabel) {
          const items = selectedColorLabel.querySelectorAll('.lpv2-bundle-color-item');
          if (items.length > 0) {
            items.forEach(item => {
              if (item.dataset.colorVal === colorValue) {
                item.classList.add('active');
              } else {
                item.classList.remove('active');
              }
            });
          } else {
            selectedColorLabel.textContent = colorValue;
          }
        }

        // Update supply cards with the variants and prices for this color
        const { v3m, v6m, v12m } = getSupplyVariantsForColor(colorValue);
        updateSupplyCardsForVariants(v3m, v6m, v12m);

        // Update device card image & price for selected color
        updateDeviceCardImage(colorValue, v3m);
        if (deviceCardPriceEl && !isOnetime) {
          deviceCardPriceEl.textContent = formatMoney(deviceCardPrice);
        }

        // Find which card is currently active and set that variant as active
        const card1 = container.querySelector('.lpv2-bundle-card[data-supply-months="3"]');
        const card2 = container.querySelector('.lpv2-bundle-card[data-supply-months="6"]');
        const card3 = container.querySelector('.lpv2-bundle-card[data-supply-months="12"]');

        if (isOnetime) {
          supplyCards.forEach(c => {
            c.classList.remove('active');
            c.setAttribute('aria-checked', 'false');
          });
          if (card1) {
            card1.classList.add('active');
            card1.setAttribute('aria-checked', 'true');
          }
        }

        const activeCard = container.querySelector('.lpv2-bundle-card.active') || card1;
        const currentActiveVariant = isOnetime
          ? v3m
          : ((activeCard === card2 ? v6m : activeCard === card3 ? v12m : v3m) || v3m);

        if (currentActiveVariant) {
          lastHandledVariantId = currentActiveVariant.id;
          container.dataset.selectedVariantId = currentActiveVariant.id;
          const currentPlanId = isOnetime ? null : (activeCard ? activeCard.dataset.planId : null);
          const currentPlan = isOnetime
            ? null
            : (findPlanInGroups(currentPlanId) || (currentPlanId ? { id: currentPlanId } : null));

          stateManager.setState({
            purchaseOption: isOnetime ? 'onetime' : 'subscription',
            selectedVariant: currentActiveVariant,
            selectedVariantId: currentActiveVariant.id,
            selectedPlanId: currentPlanId,
            selectedPlan: currentPlan,
            available: isVariantAvailable(currentActiveVariant)
          });

          const formVariantInput = document.querySelector(
            '#lpv2-add-to-cart-form input[name="id"]'
          );
          if (formVariantInput) {
            formVariantInput.value = currentActiveVariant.id;
          }
          const formSellingPlanInput = document.querySelector(
            '#lpv2-add-to-cart-form input[name="selling_plan"]'
          );
          if (formSellingPlanInput) {
            formSellingPlanInput.value = currentPlanId || '';
          }

          if (isOnetime) {
            selectOnetimeCard('onetime', v3m);
          } else {
            updateSummaryPrices(activeCard);
          }
          updateAvailability(currentActiveVariant);
        }
      });
    });

    // --- 2. Filter Supply Card Selection ---
    supplyCards.forEach(function (card) {
      card.addEventListener('click', function () {
        supplyCards.forEach(c => {
          c.classList.remove('active');
          c.setAttribute('aria-checked', 'false');
        });
        card.classList.add('active');
        card.setAttribute('aria-checked', 'true');

        const cardVariantId = card.dataset.variantId;
        let matchedVariant = null;

        if (cardVariantId) {
          lastHandledVariantId = cardVariantId;
          container.dataset.selectedVariantId = cardVariantId;
          const formVariantInput = document.querySelector(
            '#lpv2-add-to-cart-form input[name="id"]'
          );
          if (formVariantInput) {
            formVariantInput.value = cardVariantId;
          }
          if (window.LPV2ProductVariants) {
            matchedVariant = window.LPV2ProductVariants.find(
              v => String(v.id) === String(cardVariantId)
            );
          }
        }

        const supplyMonthsNum = parseInt(card.dataset.supplyMonths, 10) || 3;
        const resolvedPlan = matchedVariant
          ? findSellingPlanForVariant(matchedVariant, supplyMonthsNum)
          : null;
        const cardPlanId = resolvedPlan ? resolvedPlan.id : (card.dataset.planId || null);

        if (cardPlanId) {
          card.dataset.planId = cardPlanId;
          container.dataset.selectedPlanId = cardPlanId;
          const sellingPlanInput = document.querySelector(
            '#lpv2-add-to-cart-form input[name="selling_plan"]'
          );
          if (sellingPlanInput) {
            sellingPlanInput.value = isOnetime ? '' : cardPlanId;
          }
        } else {
          delete card.dataset.planId;
          delete container.dataset.selectedPlanId;
          const sellingPlanInput = document.querySelector(
            '#lpv2-add-to-cart-form input[name="selling_plan"]'
          );
          if (sellingPlanInput) {
            sellingPlanInput.value = '';
          }
        }

        const matchedPlan =
          resolvedPlan || findPlanInGroups(cardPlanId) || (cardPlanId ? { id: cardPlanId } : null);

        stateManager.setState({
          purchaseOption: isOnetime ? 'onetime' : 'subscription',
          selectedVariant: matchedVariant || stateManager.getState().selectedVariant,
          selectedVariantId: cardVariantId,
          selectedPlanId: isOnetime ? null : cardPlanId,
          selectedPlan: isOnetime ? null : matchedPlan,
          available: isVariantAvailable(matchedVariant)
        });

        updateSummaryPrices(card);
        updateAvailability(matchedVariant);
      });
    });

    // --- 3. Mode Toggling & Cards (Subscription vs One-time) ---
    let selectedOnetimePlan = 'onetime'; // 'onetime' or 'subscription'

    function selectOnetimeCard(planType, explicitVariant) {
      selectedOnetimePlan = planType;
      const card1 = container.querySelector('.lpv2-bundle-card[data-supply-months="3"]');
      const activeCard = container.querySelector('.lpv2-bundle-card.active') || card1 || supplyCards[0];

      if (planType === 'subscription') {
        if (onetimeSubCard) {
          onetimeSubCard.classList.add('selected');
          onetimeSubCard.classList.remove('not-selected');
          onetimeSubCard.setAttribute('aria-checked', 'true');
        }
        if (onetimeOnetimeCard) {
          onetimeOnetimeCard.classList.remove('selected');
          onetimeOnetimeCard.classList.add('not-selected');
          onetimeOnetimeCard.setAttribute('aria-checked', 'false');
        }

        const currentVariantId = activeCard?.dataset.variantId || container.dataset.selectedVariantId;
        const currentVariant = window.LPV2ProductVariants
          ? window.LPV2ProductVariants.find(v => String(v.id) === String(currentVariantId))
          : null;

        const activeMonths = activeCard
          ? parseInt(activeCard.dataset.supplyMonths, 10) || 3
          : 3;
        const resolvedPlan = currentVariant
          ? findSellingPlanForVariant(currentVariant, activeMonths)
          : null;
        const subPlanId = resolvedPlan?.id || activeCard?.dataset.planId || container.dataset.selectedPlanId;
        const subPlan = resolvedPlan || findPlanInGroups(subPlanId) || (subPlanId ? { id: subPlanId } : null);

        stateManager.setState({
          purchaseOption: 'subscription',
          selectedVariant: currentVariant || stateManager.getState().selectedVariant,
          selectedVariantId: currentVariantId,
          selectedPlanId: subPlanId,
          selectedPlan: subPlan,
          available: isVariantAvailable(currentVariant)
        });

        const formSellingPlanInput = document.querySelector(
          '#lpv2-add-to-cart-form input[name="selling_plan"]'
        );
        if (formSellingPlanInput && subPlanId) {
          formSellingPlanInput.value = subPlanId;
        }

        updateSummaryPrices(activeCard);
        updateAfterCtaText('subscription');
        updateAvailability(currentVariant);
      } else {
        // ALWAYS switch to default variant (3-month supply, first one of the chosen color)
        const activeSwatch = container.querySelector('.lpv2-bundle-swatch.active');
        const currentColor = activeSwatch ? activeSwatch.dataset.colorValue : selectedColorLabel?.textContent;
        const { v3m } = getSupplyVariantsForColor(currentColor);
        const onetimeVariant = explicitVariant || v3m || window.LPV2ProductVariants?.[0];

        // Ensure Card 1 (3-month supply) is visually active
        supplyCards.forEach(c => {
          c.classList.remove('active');
          c.setAttribute('aria-checked', 'false');
        });
        if (card1) {
          card1.classList.add('active');
          card1.setAttribute('aria-checked', 'true');
        }

        if (onetimeSubCard) {
          onetimeSubCard.classList.remove('selected');
          onetimeSubCard.classList.add('not-selected');
          onetimeSubCard.setAttribute('aria-checked', 'false');
        }
        if (onetimeOnetimeCard) {
          onetimeOnetimeCard.classList.add('selected');
          onetimeOnetimeCard.classList.remove('not-selected');
          onetimeOnetimeCard.setAttribute('aria-checked', 'true');
        }

        if (onetimeVariant) {
          lastHandledVariantId = onetimeVariant.id;
          container.dataset.selectedVariantId = onetimeVariant.id;

          stateManager.setState({
            purchaseOption: 'onetime',
            selectedVariant: onetimeVariant,
            selectedVariantId: onetimeVariant.id,
            selectedPlanId: null,
            selectedPlan: null,
            available: isVariantAvailable(onetimeVariant)
          });

          const formVariantInput = document.querySelector(
            '#lpv2-add-to-cart-form input[name="id"]'
          );
          if (formVariantInput) {
            formVariantInput.value = onetimeVariant.id;
          }
        }

        const formSellingPlanInput = document.querySelector(
          '#lpv2-add-to-cart-form input[name="selling_plan"]'
        );
        if (formSellingPlanInput) {
          formSellingPlanInput.value = '';
        }

        updateSummaryPrices(card1);
        updateAfterCtaText('onetime');
        updateAvailability(onetimeVariant);
      }
    }

    function updateAfterCtaText(planType) {
      const onetimeText = document.querySelectorAll('.lpv2-after-cta-text[data-type="one-time"]');
      const subscriptionText = document.querySelectorAll('.lpv2-after-cta-text[data-type="subscription"]');
      onetimeText.forEach(el => el.classList.toggle('active', planType === 'onetime'));
      subscriptionText.forEach(el => el.classList.toggle('active', planType === 'subscription'));
    }

    function setMode(mode) {
      isOnetime = mode === 'onetime';

      if (isOnetime) {
        container.classList.add('is-onetime');

        // ALWAYS switch to default variant (3-month supply, first one of the chosen color)
        const activeSwatch = container.querySelector('.lpv2-bundle-swatch.active');
        const currentColor = activeSwatch ? activeSwatch.dataset.colorValue : selectedColorLabel?.textContent;
        const { v3m } = getSupplyVariantsForColor(currentColor);

        selectOnetimeCard('onetime', v3m);
        updateAfterCtaText('onetime');

        if (onetimeToggle) {
          const onetimeSpan = onetimeToggle.querySelector('.lpv2-bundle-toggle-label--onetime');
          const subSpan = onetimeToggle.querySelector('.lpv2-bundle-toggle-label--sub');
          if (onetimeSpan && subSpan) {
            onetimeSpan.style.display = 'none';
            subSpan.style.display = '';
          } else {
            onetimeToggle.textContent =
              onetimeToggle.dataset.labelSub || 'Switch to smart refill plan and save';
          }
        }
      } else {
        container.classList.remove('is-onetime');
        updateAfterCtaText('subscription');

        if (onetimeSubCard) {
          onetimeSubCard.classList.add('selected');
          onetimeSubCard.classList.remove('not-selected');
          onetimeSubCard.setAttribute('aria-checked', 'true');
        }
        if (onetimeOnetimeCard) {
          onetimeOnetimeCard.classList.remove('selected');
          onetimeOnetimeCard.classList.add('not-selected');
          onetimeOnetimeCard.setAttribute('aria-checked', 'false');
        }

        const card1 = container.querySelector('.lpv2-bundle-card[data-supply-months="3"]');
        const activeCard = container.querySelector('.lpv2-bundle-card.active') || card1 || supplyCards[0];
        const activeMonths = activeCard
          ? parseInt(activeCard.dataset.supplyMonths, 10) || 3
          : 3;
        const currentVariantId = activeCard?.dataset.variantId || container.dataset.selectedVariantId;
        const currentVariant = window.LPV2ProductVariants
          ? window.LPV2ProductVariants.find(v => String(v.id) === String(currentVariantId))
          : null;
        const resolvedPlan = currentVariant
          ? findSellingPlanForVariant(currentVariant, activeMonths)
          : null;
        const activePlanId =
          resolvedPlan?.id || activeCard?.dataset.planId || container.dataset.selectedPlanId;
        const activePlan =
          resolvedPlan || findPlanInGroups(activePlanId) || (activePlanId ? { id: activePlanId } : null);

        stateManager.setState({
          purchaseOption: 'subscription',
          selectedVariant: currentVariant || stateManager.getState().selectedVariant,
          selectedVariantId: currentVariantId,
          selectedPlanId: activePlanId,
          selectedPlan: activePlan,
          available: isVariantAvailable(currentVariant)
        });

        const formSellingPlanInput = document.querySelector(
          '#lpv2-add-to-cart-form input[name="selling_plan"]'
        );
        if (formSellingPlanInput && activePlanId) {
          formSellingPlanInput.value = activePlanId;
        }

        const activeOnetimePrice = (card1 ? parseInt(card1.dataset.onetimePrice, 10) : null) || onetimePrice;

        if (onetimeToggle) {
          const onetimeSpan = onetimeToggle.querySelector('.lpv2-bundle-toggle-label--onetime');
          const subSpan = onetimeToggle.querySelector('.lpv2-bundle-toggle-label--sub');
          if (onetimeSpan && subSpan) {
            onetimeSpan.style.display = '';
            subSpan.style.display = 'none';
            const priceSpan = onetimeToggle.querySelector('.lpv2-bundle-toggle-price');
            if (priceSpan) {
              priceSpan.textContent = formatMoney(activeOnetimePrice);
            }
          } else {
            const tpl = onetimeToggle.dataset.labelOnetime || 'I prefer a one-time purchase ([price])';
            onetimeToggle.textContent = replacePriceInText(tpl, formatMoney(activeOnetimePrice));
          }
        }

        updateSummaryPrices(activeCard);
        updateAvailability(currentVariant);
      }
    }

    if (onetimeToggle) {
      onetimeToggle.addEventListener('click', function (e) {
        e.preventDefault();
        setMode(isOnetime ? 'subscription' : 'onetime');
      });
    }

    if (onetimeSubCard) {
      onetimeSubCard.addEventListener('click', function () {
        setMode('subscription');
      });
      onetimeSubCard.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setMode('subscription');
        }
      });
    }

    if (onetimeOnetimeCard) {
      onetimeOnetimeCard.addEventListener('click', function () {
        selectOnetimeCard('onetime');
      });
      onetimeOnetimeCard.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectOnetimeCard('onetime');
        }
      });
    }

    function scrollToBundle() {
      setTimeout(function () {
        const bundleEl = container || document.querySelector('.lpv2-main-product-options-bundle, .pv2-main-product-options-bundle');
        if (!bundleEl) return;
        const header = document.querySelector(
          '.mega-menu-outer-shopify-wrapper, .mega-menu, header, .header-wrapper'
        );
        const headerOffset = header ? header.offsetHeight : 70;
        const targetTop =
          bundleEl.getBoundingClientRect().top +
          (window.pageYOffset || document.documentElement.scrollTop) -
          headerOffset -
          16;
        window.scrollTo({
          top: targetTop > 0 ? targetTop : 0,
          behavior: 'smooth'
        });
      }, 50);
    }

    // Support clicks on oos-dispatch block buttons
    document.addEventListener('click', function (e) {
      const preferOnetime = e.target.closest('.js-prefer-one-time');
      if (preferOnetime) {
        e.preventDefault();
        setMode('onetime');
        scrollToBundle();
        return;
      }
      const preferSub = e.target.closest('.js-prefer-subscription');
      if (preferSub) {
        e.preventDefault();
        setMode('subscription');
        scrollToBundle();
        return;
      }
    });

    // Initialize default mode (onetime vs subscription)
    const isDefaultOnetime =
      container.classList.contains('is-onetime') ||
      container.dataset.defaultPurchaseOption === 'onetime';
    if (isDefaultOnetime) {
      setMode('onetime');
    } else {
      updateAfterCtaText('subscription');
    }

    // --- 4. Main CTA Button Click ---
    if (atcBtn) {
      atcBtn.addEventListener('click', function (e) {
        e.preventDefault();

        // Sync selling plan ID to form and stateManager
        const form = document.querySelector('#lpv2-add-to-cart-form');
        const sellingPlanInput = form ? form.querySelector('input[name="selling_plan"]') : null;
        const variantInput = form ? form.querySelector('input[name="id"]') : null;

        const activeCard =
          container.querySelector('.lpv2-bundle-card.active') || supplyCards[0];
        const currentVariantId = isOnetime
          ? (container.dataset.selectedVariantId || activeCard?.dataset.variantId)
          : (activeCard?.dataset.variantId || container.dataset.selectedVariantId);

        const matchedVariant = window.LPV2ProductVariants
          ? window.LPV2ProductVariants.find(v => String(v.id) === String(currentVariantId))
          : null;

        let atcSellingPlanId = null;
        let atcMatchedPlan = null;
        let atcPurchaseOption = 'subscription';

        if (isOnetime) {
          if (selectedOnetimePlan === 'subscription') {
            const subPlan = matchedVariant ? findSellingPlanForVariant(matchedVariant, 3) : null;
            atcSellingPlanId = subPlan ? subPlan.id : (container.dataset.selectedPlanId || null);
            atcMatchedPlan = subPlan || findPlanInGroups(atcSellingPlanId) || (atcSellingPlanId ? { id: atcSellingPlanId } : null);
            atcPurchaseOption = 'subscription';
          } else {
            atcSellingPlanId = null;
            atcMatchedPlan = null;
            atcPurchaseOption = 'onetime';
          }
        } else {
          const activeMonths = activeCard
            ? parseInt(activeCard.dataset.supplyMonths, 10) || 3
            : 3;
          const resolvedPlan = matchedVariant
            ? findSellingPlanForVariant(matchedVariant, activeMonths)
            : null;
          atcSellingPlanId = resolvedPlan ? resolvedPlan.id : (activeCard?.dataset.planId || null);
          atcMatchedPlan = resolvedPlan || findPlanInGroups(atcSellingPlanId) || (atcSellingPlanId ? { id: atcSellingPlanId } : null);
          atcPurchaseOption = 'subscription';
        }

        if (variantInput && currentVariantId) {
          variantInput.value = currentVariantId;
        }

        if (sellingPlanInput) {
          sellingPlanInput.value = atcSellingPlanId || '';
        }

        stateManager.setState({
          purchaseOption: atcPurchaseOption,
          selectedVariant: matchedVariant || stateManager.getState().selectedVariant,
          selectedVariantId: currentVariantId,
          selectedPlanId: atcSellingPlanId,
          selectedPlan: atcMatchedPlan,
          selectedFreebies: []
        });

        // Trigger the main Add to Cart button so existing drawer / analytics logic runs
        const mainBtn = document.querySelector('.lpv2-main-product-buttons .lpv2-btn--add-to-cart');
        if (mainBtn) {
          mainBtn.click();
        } else if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    }

    if (notifyBtn) {
      notifyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        initNotifyModalForm();

        const activeCard =
          container.querySelector('.lpv2-bundle-card.active') || supplyCards[0];
        const currentVariantId = isOnetime
          ? (container.dataset.selectedVariantId || activeCard?.dataset.variantId)
          : (activeCard?.dataset.variantId || container.dataset.selectedVariantId);

        const currentVariant = window.LPV2ProductVariants
          ? window.LPV2ProductVariants.find(v => String(v.id) === String(currentVariantId))
          : null;

        if (currentVariant) {
          updateAvailability(currentVariant);
        }

        if (window.modalController && typeof window.modalController.presentModal === 'function') {
          window.modalController.presentModal('product-notify');
        } else {
          const modalTrigger = document.querySelector(
            '[data-modal-trigger="product-notify"]:not([data-bundle-notify-btn])'
          );
          if (modalTrigger) {
            modalTrigger.click();
          }
        }
      });
    }

    // --- 5. Sync with global variant & state changes ---
    function handleVariantUpdate(variant) {
      if (!variant || !variant.id) return;
      if (String(lastHandledVariantId) === String(variant.id)) return;
      lastHandledVariantId = variant.id;

      const colorVal = variant.option1 || variant.title;
      if (colorVal) {
        colorSwatches.forEach(function (s) {
          if (s.dataset.colorValue === colorVal) {
            s.classList.add('active');
            s.setAttribute('aria-checked', 'true');
            if (selectedColorLabel) {
              const items = selectedColorLabel.querySelectorAll('.lpv2-bundle-color-item');
              if (items.length > 0) {
                items.forEach(item => {
                  if (item.dataset.colorVal === colorVal) {
                    item.classList.add('active');
                  } else {
                    item.classList.remove('active');
                  }
                });
              } else {
                selectedColorLabel.textContent = colorVal;
              }
            }
          } else {
            s.classList.remove('active');
            s.setAttribute('aria-checked', 'false');
          }
        });
      }

      let { v3m, v6m, v12m } = colorVal ? getSupplyVariantsForColor(colorVal) : { v3m: null, v6m: null, v12m: null };
      if (!v3m) v3m = variant;
      if (!v6m) v6m = v3m;
      if (!v12m) v12m = v6m;
      updateSupplyCardsForVariants(v3m, v6m, v12m);

      // Update device card image for selected color
      const activeSwatch = container.querySelector('.lpv2-bundle-swatch.active');
      const activeColor = activeSwatch ? activeSwatch.dataset.colorValue : colorVal;
      updateDeviceCardImage(activeColor, v3m, variant);

      const card1 = container.querySelector('.lpv2-bundle-card[data-supply-months="3"]');
      const activeCard = container.querySelector('.lpv2-bundle-card.active') || card1;
      const targetVariant = isOnetime
        ? (v3m || variant)
        : ((activeCard?.dataset.supplyMonths === '6' ? v6m : activeCard?.dataset.supplyMonths === '12' ? v12m : v3m) || variant);
      updateSummaryPrices(activeCard);
      updateAvailability(targetVariant);
    }

    // Direct listener for variantChanged event (e.detail is the current variant object)
    document.addEventListener('variantChanged', function (e) {
      const variant = e.detail;
      if (variant) {
        handleVariantUpdate(variant);
      }
    });

    // Also sync with lpv2-state-changed for broader state changes
    document.addEventListener('lpv2-state-changed', function (e) {
      const state = e.detail;
      if (state && state.selectedVariant) {
        handleVariantUpdate(state.selectedVariant);
      }
    });
  });
}

window.initOptionsBundleBlock = initOptionsBundleBlock;
